<div class="w-full px-4 lg:pr-6 flex flex-col gap-6 overflow-x-hidden">
  <!-- Header with View Toggle -->
  <div class="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
    <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
      <div>
        <h1 class="text-2xl font-bold text-gray-900">{{'attendanceManagement' | translate}}</h1>
        <p class="text-gray-600 text-sm mt-1">{{'trackAndManageAttendance' | translate}}</p>
      </div>
      
      <!-- View Toggle Buttons -->
      <div class="flex flex-wrap items-center gap-2 bg-gray-100 rounded-lg p-1">
        <button 
          (click)="switchView('employee')"
          [class]="currentView === 'employee' ? 'bg-white shadow-sm text-primary' : 'text-gray-600 hover:text-gray-900'"
          class="px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center gap-2">
          <mat-icon class="text-lg">person</mat-icon>
          <span>{{'myAttendance' | translate}}</span>
        </button>
        
        <button 
          *ngIf="canAccessSupervisorView()"
          (click)="switchView('supervisor')"
          [class]="currentView === 'supervisor' ? 'bg-white shadow-sm text-primary' : 'text-gray-600 hover:text-gray-900'"
          class="px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center gap-2">
          <mat-icon class="text-lg">supervisor_account</mat-icon>
          <span>{{'teamAttendance' | translate}}</span>
          <span *ngIf="pendingApprovalsCount > 0" 
                class="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
            {{pendingApprovalsCount}}
          </span>
        </button>
        
        <button 
          *ngIf="canAccessProjectView()"
          (click)="switchView('project')"
          [class]="currentView === 'project' ? 'bg-white shadow-sm text-primary' : 'text-gray-600 hover:text-gray-900'"
          class="px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center gap-2">
          <mat-icon class="text-lg">work</mat-icon>
          <span>{{'projectAttendance' | translate}}</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Filters Section -->
  <div class="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
    <div class="flex flex-col lg:flex-row flex-wrap gap-4 items-stretch lg:items-center">
      <!-- Search -->
      <div class="flex-1 min-w-[120px]">
        <div class="relative h-[42px]">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <mat-icon class="text-gray-400 !text-xl !w-5 !h-5">search</mat-icon>
          </div>
          <input 
            type="text"
            class="w-full h-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-colors" 
            placeholder="{{'searchByNameOrLocation' | translate}}" 
            (keyup)="applyFilter($event)" />
        </div>
      </div>

      <!-- Project Filter (for Supervisor/Project views) -->
      <div *ngIf="currentView !== 'employee'" class="w-full lg:w-[40px] min-w-[150px]">
        <select 
          [(ngModel)]="selectedProjectId"
          (change)="onProjectChange()"
          class="w-full h-[42px] px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none">
          <option [ngValue]="null">{{'allProjects' | translate}}</option>
          <option *ngFor="let project of projects" [ngValue]="project.id">
            {{project.project_name}}
          </option>
        </select>
      </div>

      <!-- Employee Filter (for Supervisor view) -->
      <div *ngIf="currentView === 'supervisor'" class="w-full lg:w-[40px] min-w-[150px]">
        <select 
          [(ngModel)]="selectedEmployeeId"
          (change)="onEmployeeChange()"
          class="w-full h-[42px] px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none">
          <option [ngValue]="null">{{'allEmployees' | translate}}</option>
          <option *ngFor="let emp of employees" [ngValue]="emp.id">
            {{emp.full_name}}
          </option>
        </select>
      </div>

      <!-- Date Range -->
      <div class="w-full lg:w-80 min-w-[200px]">
        <!-- <mat-label>{{'dateRange' | translate}}</mat-label> -->
        <mat-form-field appearance="fill" [formGroup]="range" class="w-full custom-date-field attendance-date-field">
          <mat-date-range-input [rangePicker]="picker">
            <input matStartDate formControlName="start" placeholder="{{'startDate' | translate}}">
            <input matEndDate formControlName="end" placeholder="{{'endDate' | translate}}" (dateChange)="onDateRangeChange()">
          </mat-date-range-input>
          <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
          <mat-date-range-picker #picker></mat-date-range-picker>
        </mat-form-field>
      </div>

      <!-- Action Buttons -->
      <div class="flex gap-2 flex-shrink-0">
        <button 
          (click)="clearFilters()"
          class="attendance-action-btn inline-flex items-center justify-center gap-2 px-4 py-2 h-[42px] border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors">
          <mat-icon class="attendance-btn-icon">clear</mat-icon>
          <span>{{'clear' | translate}}</span>
        </button>
        <button 
          (click)="exportToCSV()"
          class="attendance-action-btn inline-flex items-center justify-center gap-2 px-4 py-2 h-[42px] bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors">
          <mat-icon class="attendance-btn-icon">download</mat-icon>
          <span>{{'export' | translate}}</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Loading Spinner -->
  <div *ngIf="isLoading" class="flex justify-center items-center py-12">
    <mat-spinner diameter="40"></mat-spinner>
  </div>

  <!-- ========== EMPLOYEE VIEW ========== -->
  <div *ngIf="currentView === 'employee' && !isLoading" class="space-y-6">
    <!-- Employee Summary Card -->
    <div *ngIf="employeeSummary" class="grid grid-cols-1 md:grid-cols-4 gap-4">
      <div class="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div class="flex items-center gap-3">
          <div class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
            <mat-icon class="text-blue-600">calendar_today</mat-icon>
          </div>
          <div>
            <p class="text-sm text-gray-600">{{'totalDays' | translate}}</p>
            <p class="text-2xl font-bold text-gray-900">{{employeeSummary.total_days}}</p>
          </div>
        </div>
      </div>
      
      <div class="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div class="flex items-center gap-3">
          <div class="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
            <mat-icon class="text-green-600">schedule</mat-icon>
          </div>
          <div>
            <p class="text-sm text-gray-600">{{'totalHours' | translate}}</p>
            <p class="text-2xl font-bold text-gray-900">{{employeeSummary.total_hours | number:'1.1-1'}}h</p>
          </div>
        </div>
      </div>
      
      <div class="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div class="flex items-center gap-3">
          <div class="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
            <mat-icon class="text-purple-600">trending_up</mat-icon>
          </div>
          <div>
            <p class="text-sm text-gray-600">{{'avgHoursPerDay' | translate}}</p>
            <p class="text-2xl font-bold text-gray-900">{{employeeSummary.avg_hours_per_day | number:'1.1-1'}}h</p>
          </div>
        </div>
      </div>
      
      <div class="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div class="flex items-center gap-3">
          <div class="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
            <mat-icon class="text-teal-600">thumb_up</mat-icon>
          </div>
          <div>
            <p class="text-sm text-gray-600">{{'onTimeArrivals' | translate}}</p>
            <p class="text-2xl font-bold text-gray-900">{{employeeSummary.on_time_count}}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Employee Attendance Table -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div class="px-6 py-4 border-b border-gray-200">
        <div class="flex items-center gap-2">
          <div class="w-1 h-6 bg-primary rounded"></div>
          <h2 class="text-lg font-semibold text-gray-900">{{'myAttendanceRecords' | translate}}</h2>
        </div>
      </div>
      
      <div class="overflow-x-auto">
        <table mat-table [dataSource]="employeeDataSource" matSort class="w-full">
          <ng-container matColumnDef="date">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="!px-6 !py-4 !text-left !text-xs !font-medium !text-gray-500 !uppercase">{{'DATE' | translate}}</th>
            <td mat-cell *matCellDef="let element" class="!px-6 !py-4 !whitespace-nowrap !text-sm !text-gray-900">
              {{element.date | date:'mediumDate'}}
            </td>
          </ng-container>

          <ng-container matColumnDef="timeIn">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="!px-6 !py-4 !text-left !text-xs !font-medium !text-gray-500 !uppercase">{{'timeIn' | translate}}</th>
            <td mat-cell *matCellDef="let element" class="!px-6 !py-4 !whitespace-nowrap !text-sm !text-gray-900">
              <span class="inline-flex items-center gap-1">
                <mat-icon class="text-green-500 !text-base">login</mat-icon>
                {{formatTime(element.intime)}}
              </span>
            </td>
          </ng-container>

          <ng-container matColumnDef="timeOut">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="!px-6 !py-4 !text-left !text-xs !font-medium !text-gray-500 !uppercase">{{'timeOut' | translate}}</th>
            <td mat-cell *matCellDef="let element" class="!px-6 !py-4 !whitespace-nowrap !text-sm !text-gray-900">
              <span class="inline-flex items-center gap-1">
                <mat-icon class="text-red-500 !text-base">logout</mat-icon>
                {{formatTime(element.outtime)}}
              </span>
            </td>
          </ng-container>

          <ng-container matColumnDef="location">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="!px-6 !py-4 !text-left !text-xs !font-medium !text-gray-500 !uppercase">{{'LOCATION' | translate}}</th>
            <td mat-cell *matCellDef="let element" class="!px-6 !py-4 !whitespace-nowrap !text-sm !text-gray-600">
              <span class="inline-flex items-center gap-1">
                <mat-icon class="text-gray-400 !text-base">location_on</mat-icon>
                {{element.location || '-'}}
              </span>
            </td>
          </ng-container>

          <ng-container matColumnDef="hoursWorked">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="!px-6 !py-4 !text-left !text-xs !font-medium !text-gray-500 !uppercase">{{'hoursWorked' | translate}}</th>
            <td mat-cell *matCellDef="let element" class="!px-6 !py-4 !whitespace-nowrap !text-sm !font-medium !text-gray-900">
              {{formatHours(element.working_hours)}}
            </td>
          </ng-container>

          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="!px-6 !py-4 !text-left !text-xs !font-medium !text-gray-500 !uppercase">{{'status' | translate}}</th>
            <td mat-cell *matCellDef="let element" class="!px-6 !py-4 !whitespace-nowrap">
              <span class="px-2.5 py-1 rounded-full text-xs font-medium" [ngClass]="getStatusClass(element.status)">
                {{element.status || 'Present'}}
              </span>
            </td>
          </ng-container>

          <ng-container matColumnDef="remarks">
            <th mat-header-cell *matHeaderCellDef class="!px-6 !py-4 !text-left !text-xs !font-medium !text-gray-500 !uppercase">{{'remarks' | translate}}</th>
            <td mat-cell *matCellDef="let element" class="!px-6 !py-4 !text-sm !text-gray-600 !max-w-xs !truncate">
              {{element.remark || '-'}}
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="employeeDisplayedColumns" class="bg-gray-50"></tr>
          <tr mat-row *matRowDef="let row; columns: employeeDisplayedColumns;" class="hover:bg-gray-50 transition-colors"></tr>
        </table>
      </div>
<!-- 
         -->
      <app-empty-data *ngIf="employeeDataSource.data.length === 0" 
        class="flex justify-center py-12"
        [imageSource]="'../../assets/blankScreen/support.png'" 
        [message]="'noAttendanceRecordsFound' | translate">
      </app-empty-data>

      <mat-paginator [pageSizeOptions]="[5, 10, 25]" showFirstLastButtons class="border-t border-gray-200"></mat-paginator>
    </div>
  </div>

  <!-- ========== SUPERVISOR VIEW ========== -->
  <div *ngIf="currentView === 'supervisor' && !isLoading" class="space-y-6">
    <!-- Supervisor Stats -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div class="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600">{{'totalRecords' | translate}}</p>
            <p class="text-2xl font-bold text-gray-900">{{supervisorDataSource.data.length}}</p>
          </div>
          <div class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
            <mat-icon class="text-blue-600">assignment</mat-icon>
          </div>
        </div>
      </div>
      
      <div class="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600">{{'pendingApprovals' | translate}}</p>
            <p class="text-2xl font-bold text-yellow-600">{{pendingApprovalsCount}}</p>
          </div>
          <div class="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
            <mat-icon class="text-yellow-600">pending_actions</mat-icon>
          </div>
        </div>
      </div>
      
      <div class="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600">{{'approved' | translate}}</p>
            <p class="text-2xl font-bold text-green-600">
              {{supervisorDataSource.data.length - pendingApprovalsCount}}
            </p>
          </div>
          <div class="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
            <mat-icon class="text-green-600">check_circle</mat-icon>
          </div>
        </div>
      </div>
    </div>

    <!-- Bulk Actions -->
    <div *ngIf="pendingApprovalsCount > 0" class="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <mat-icon class="text-yellow-600">info</mat-icon>
          <span class="text-yellow-800">
            {{'youHave' | translate}} <strong>{{pendingApprovalsCount}}</strong> {{'pendingAttendanceApprovals' | translate}}
          </span>
        </div>
        <button 
          (click)="bulkApprove()"
          class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2">
          <mat-icon class="text-sm">done_all</mat-icon>
          {{'approveAll' | translate}}
        </button>
      </div>
    </div>

    <!-- Supervisor Attendance Table -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div class="px-6 py-4 border-b border-gray-200">
        <div class="flex items-center gap-2">
          <div class="w-1 h-6 bg-primary rounded"></div>
          <h2 class="text-lg font-semibold text-gray-900">{{'teamAttendanceRecords' | translate}}</h2>
        </div>
      </div>
      
      <div class="overflow-x-auto">
        <table mat-table [dataSource]="supervisorDataSource" matSort class="w-full">
          <ng-container matColumnDef="employeeName">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="!px-6 !py-4 !text-left !text-xs !font-medium !text-gray-500 !uppercase">{{'NAME' | translate}}</th>
            <td mat-cell *matCellDef="let element" class="!px-6 !py-4 !whitespace-nowrap">
              <div class="flex items-center gap-3">
                <div class="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                  <span class="text-sm font-medium text-primary">{{element.employeeName?.charAt(0) | uppercase}}</span>
                </div>
                <div>
                  <p class="text-sm font-medium text-gray-900">{{element.employeeName}}</p>
                  <p class="text-xs text-gray-500">ID: {{element.employeeId}}</p>
                </div>
              </div>
            </td>
          </ng-container>

          <ng-container matColumnDef="date">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="!px-6 !py-4 !text-left !text-xs !font-medium !text-gray-500 !uppercase">{{'DATE' | translate}}</th>
            <td mat-cell *matCellDef="let element" class="!px-6 !py-4 !whitespace-nowrap !text-sm !text-gray-900">
              {{element.date | date:'mediumDate'}}
            </td>
          </ng-container>

          <ng-container matColumnDef="timeIn">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="!px-6 !py-4 !text-left !text-xs !font-medium !text-gray-500 !uppercase">{{'timeIn' | translate}}</th>
            <td mat-cell *matCellDef="let element" class="!px-6 !py-4 !whitespace-nowrap !text-sm !text-gray-900">
              {{element.timeIn}}
            </td>
          </ng-container>

          <ng-container matColumnDef="timeOut">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="!px-6 !py-4 !text-left !text-xs !font-medium !text-gray-500 !uppercase">{{'timeOut' | translate}}</th>
            <td mat-cell *matCellDef="let element" class="!px-6 !py-4 !whitespace-nowrap !text-sm !text-gray-900">
              {{element.timeOut}}
            </td>
          </ng-container>

          <ng-container matColumnDef="location">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="!px-6 !py-4 !text-left !text-xs !font-medium !text-gray-500 !uppercase">{{'LOCATION' | translate}}</th>
            <td mat-cell *matCellDef="let element" class="!px-6 !py-4 !whitespace-nowrap !text-sm !text-gray-600">
              {{element.location}}
            </td>
          </ng-container>

          <ng-container matColumnDef="hoursWorked">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="!px-6 !py-4 !text-left !text-xs !font-medium !text-gray-500 !uppercase">{{'hoursWorked' | translate}}</th>
            <td mat-cell *matCellDef="let element" class="!px-6 !py-4 !whitespace-nowrap !text-sm !text-gray-900">
              {{element.hoursWorked | number:'1.1-1'}}h
            </td>
          </ng-container>

          <ng-container matColumnDef="adjustedHours">
            <th mat-header-cell *matHeaderCellDef class="!px-6 !py-4 !text-left !text-xs !font-medium !text-gray-500 !uppercase">{{'adjustedHours' | translate}}</th>
            <td mat-cell *matCellDef="let element" class="!px-6 !py-4 !whitespace-nowrap">
              <input 
                type="number" 
                [(ngModel)]="element.adjustedHours" 
                (change)="adjustHours(element, element.adjustedHours)"
                [disabled]="element.approvalStatus === 'approved'"
                class="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:bg-gray-100"
                step="0.5"
                min="-4"
                max="4">
            </td>
          </ng-container>

          <ng-container matColumnDef="totalHours">
            <th mat-header-cell *matHeaderCellDef class="!px-6 !py-4 !text-left !text-xs !font-medium !text-gray-500 !uppercase">{{'totalHours' | translate}}</th>
            <td mat-cell *matCellDef="let element" class="!px-6 !py-4 !whitespace-nowrap !text-sm !font-bold !text-gray-900">
              {{element.totalHours | number:'1.1-1'}}h
            </td>
          </ng-container>

          <ng-container matColumnDef="approvalStatus">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="!px-6 !py-4 !text-left !text-xs !font-medium !text-gray-500 !uppercase">{{'status' | translate}}</th>
            <td mat-cell *matCellDef="let element" class="!px-6 !py-4 !whitespace-nowrap">
              <span class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium" [ngClass]="getStatusClass(element.approvalStatus)">
                <mat-icon class="!text-sm">{{getApprovalStatusIcon(element.approvalStatus)}}</mat-icon>
                {{element.approvalStatus | titlecase}}
              </span>
            </td>
          </ng-container>

          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef class="!px-6 !py-4 !text-left !text-xs !font-medium !text-gray-500 !uppercase">{{'actions' | translate}}</th>
            <td mat-cell *matCellDef="let element" class="!px-6 !py-4 !whitespace-nowrap">
              <div *ngIf="element.approvalStatus === 'pending'" class="flex items-center gap-2">
                <button 
                  (click)="approveAttendance(element)"
                  class="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                  title="Approve">
                  <mat-icon class="!text-lg">check_circle</mat-icon>
                </button>
                <button 
                  (click)="rejectAttendance(element)"
                  class="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Reject">
                  <mat-icon class="!text-lg">cancel</mat-icon>
                </button>
              </div>
              <span *ngIf="element.approvalStatus !== 'pending'" class="text-gray-400 text-sm">
                {{element.approvedBy}}
              </span>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="supervisorDisplayedColumns" class="bg-gray-50"></tr>
          <tr mat-row *matRowDef="let row; columns: supervisorDisplayedColumns;" 
              class="hover:bg-gray-50 transition-colors"
              [class.bg-yellow-50]="row.approvalStatus === 'pending'"></tr>
        </table>
      </div>

      <app-empty-data *ngIf="supervisorDataSource.data.length === 0" 
        class="flex justify-center py-12"
        [imageSource]="'../../assets/blankScreen/support.png'" 
        [message]="'noTeamAttendanceRecordsFound' | translate">
      </app-empty-data>

      <mat-paginator [pageSizeOptions]="[5, 10, 25]" showFirstLastButtons class="border-t border-gray-200"></mat-paginator>
    </div>
  </div>

  <!-- ========== PROJECT VIEW ========== -->
  <div *ngIf="currentView === 'project' && !isLoading" class="space-y-6">
    <!-- Project Stats -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div class="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600">{{'totalEmployees' | translate}}</p>
            <p class="text-2xl font-bold text-gray-900">{{projectStats.totalEmployees}}</p>
          </div>
          <div class="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
            <mat-icon class="text-indigo-600">groups</mat-icon>
          </div>
        </div>
      </div>
      
      <div class="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600">{{'totalHoursLogged' | translate}}</p>
            <p class="text-2xl font-bold text-gray-900">{{projectStats.totalHours | number:'1.1-1'}}h</p>
          </div>
          <div class="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
            <mat-icon class="text-green-600">timer</mat-icon>
          </div>
        </div>
      </div>
      
      <div class="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600">{{'avgHoursPerEmployee' | translate}}</p>
            <p class="text-2xl font-bold text-gray-900">{{projectStats.avgHours | number:'1.1-1'}}h</p>
          </div>
          <div class="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
            <mat-icon class="text-purple-600">insights</mat-icon>
          </div>
        </div>
      </div>
    </div>

    <!-- Project Attendance Table -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div class="px-6 py-4 border-b border-gray-200">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <div class="w-1 h-6 bg-primary rounded"></div>
            <h2 class="text-lg font-semibold text-gray-900">{{'projectAttendanceRecords' | translate}}</h2>
          </div>
          <div *ngIf="selectedProjectId" class="text-sm text-gray-600">
            {{'project' | translate}}: <strong>{{getSelectedProjectName()}}</strong>
          </div>
        </div>
      </div>
      
      <div class="overflow-x-auto">
        <table mat-table [dataSource]="projectDataSource" matSort class="w-full">
          <ng-container matColumnDef="fullName">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="!px-6 !py-4 !text-left !text-xs !font-medium !text-gray-500 !uppercase">{{'NAME' | translate}}</th>
            <td mat-cell *matCellDef="let element" class="!px-6 !py-4 !whitespace-nowrap">
              <div class="flex items-center gap-3">
                <div class="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                  <span class="text-sm font-medium text-primary">{{(element.full_name || element.user_name)?.charAt(0) | uppercase}}</span>
                </div>
                <span class="text-sm font-medium text-gray-900">{{element.full_name || element.user_name}}</span>
              </div>
            </td>
          </ng-container>

          <ng-container matColumnDef="date">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="!px-6 !py-4 !text-left !text-xs !font-medium !text-gray-500 !uppercase">{{'DATE' | translate}}</th>
            <td mat-cell *matCellDef="let element" class="!px-6 !py-4 !whitespace-nowrap !text-sm !text-gray-900">
              {{element.date | date:'mediumDate'}}
            </td>
          </ng-container>

          <ng-container matColumnDef="timeIn">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="!px-6 !py-4 !text-left !text-xs !font-medium !text-gray-500 !uppercase">{{'timeIn' | translate}}</th>
            <td mat-cell *matCellDef="let element" class="!px-6 !py-4 !whitespace-nowrap !text-sm !text-gray-900">
              {{formatTime(element.intime)}}
            </td>
          </ng-container>

          <ng-container matColumnDef="timeOut">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="!px-6 !py-4 !text-left !text-xs !font-medium !text-gray-500 !uppercase">{{'timeOut' | translate}}</th>
            <td mat-cell *matCellDef="let element" class="!px-6 !py-4 !whitespace-nowrap !text-sm !text-gray-900">
              {{formatTime(element.outtime)}}
            </td>
          </ng-container>

          <ng-container matColumnDef="location">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="!px-6 !py-4 !text-left !text-xs !font-medium !text-gray-500 !uppercase">{{'LOCATION' | translate}}</th>
            <td mat-cell *matCellDef="let element" class="!px-6 !py-4 !whitespace-nowrap !text-sm !text-gray-600">
              {{element.location || '-'}}
            </td>
          </ng-container>

          <ng-container matColumnDef="hoursWorked">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="!px-6 !py-4 !text-left !text-xs !font-medium !text-gray-500 !uppercase">{{'hoursWorked' | translate}}</th>
            <td mat-cell *matCellDef="let element" class="!px-6 !py-4 !whitespace-nowrap !text-sm !font-medium !text-gray-900">
              {{formatHours(element.working_hours)}}
            </td>
          </ng-container>

          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="!px-6 !py-4 !text-left !text-xs !font-medium !text-gray-500 !uppercase">{{'status' | translate}}</th>
            <td mat-cell *matCellDef="let element" class="!px-6 !py-4 !whitespace-nowrap">
              <span class="px-2.5 py-1 rounded-full text-xs font-medium" [ngClass]="getStatusClass(element.status)">
                {{element.status || 'Present'}}
              </span>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="projectDisplayedColumns" class="bg-gray-50"></tr>
          <tr mat-row *matRowDef="let row; columns: projectDisplayedColumns;" class="hover:bg-gray-50 transition-colors"></tr>
        </table>
      </div>

      <app-empty-data *ngIf="projectDataSource.data.length === 0" 
        class="flex justify-center py-12"
        [imageSource]="'../../assets/blankScreen/support.png'" 
        [message]="'noProjectAttendanceRecordsFound' | translate">
      </app-empty-data>

      <mat-paginator [pageSizeOptions]="[5, 10, 25]" showFirstLastButtons class="border-t border-gray-200"></mat-paginator>
    </div>
  </div>
</div>
